import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from 'services/_infra/rtkBaseQuery';
import { buildLocationAPIPayload } from 'store/utils/userLocation.utils';
import { groupItemsByKey } from 'utils/data/parse.utils';
import { normalizeEarthquakes } from 'services/weather/weather.service';

const normalizeAirQualityForecastResponse = (rawData) =>
  [
    ...groupItemsByKey(
      rawData,
      (item) => item.air_station_name,
      ({ iso8601, category, concentration }) => ({ iso8601, category, concentration }),
    ),
  ].map(([air_station_name, forecast_report]) => ({ air_station_name, forecast_report }));

export const weatherApi = createApi({
  reducerPath: 'weatherApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['Weather'],
  endpoints: (builder) => ({
    getHourlyForecast: builder.query({
      query: (location) => ({
        url: '/v2/custom-weather/forecast/hourly/48-hour',
        params: buildLocationAPIPayload(location),
      }),
      transformResponse: (response) => response?.result,
      providesTags: ['Weather'],
    }),
    getConditionsbyCity: builder.query({
      query: (location) => ({
        url: '/v2/custom-weather/current-conditions',
        params: buildLocationAPIPayload(location),
      }),
      keepUnusedDataFor: 60 * 10,
      transformResponse: (response) => response?.result,
      providesTags: ['Weather'],
    }),
    getDetailedForecast: builder.query({
      query: (location) => ({
        url: '/v2/custom-weather/forecast/detailed',
        params: buildLocationAPIPayload(location),
      }),
      transformResponse: (response) => response?.result,
      providesTags: ['Weather'],
    }),
    get15DayForecast: builder.query({
      query: (location) => ({
        url: '/v2/custom-weather/forecast/15-day',
        params: buildLocationAPIPayload(location),
      }),
      transformResponse: (response) => response?.result,
      providesTags: ['Weather'],
    }),
    get7DayHourlyForecast: builder.query({
      query: (location) => ({
        url: '/v2/custom-weather/forecast/hourly/7-day',
        params: buildLocationAPIPayload(location),
      }),
      transformResponse: (response) => response?.result,
      providesTags: ['Weather'],
    }),
    getAstronomyData: builder.query({
      query: (location) => ({
        url: '/v2/custom-weather/astronomy',
        params: buildLocationAPIPayload(location),
      }),
      transformResponse: (response) => response?.result,
      providesTags: ['Weather'],
    }),
    getAirQualityForecast: builder.query({
      query: (location) => ({
        url: '/v2/custom-weather/air-quality/forecast',
        params: buildLocationAPIPayload(location),
      }),
      transformResponse: (response) => normalizeAirQualityForecastResponse(response?.result),
      providesTags: ['Weather'],
    }),
    getAirQualityObservation: builder.query({
      query: (location) => ({
        url: '/v2/custom-weather/air-quality/observation',
        params: buildLocationAPIPayload(location),
      }),
      transformResponse: (response) => (Array.isArray(response?.result) ? response.result : []),
      providesTags: ['Weather'],
    }),
    getEarthquakes: builder.query({
      query: () => ({
        url: '/v2/custom-weather/earthquakes',
      }),
      keepUnusedDataFor: 60 * 10,
      transformResponse: (response) => normalizeEarthquakes(response?.result),
      providesTags: [{ type: 'Weather', id: 'EARTHQUAKES' }], // Only refetches when ID is EARTHQUAKES
    }),
    getHurricanesHistorical: builder.query({
      query: () => ({
        url: '/v2/custom-weather/hurricanes/historical',
      }),
      keepUnusedDataFor: 60 * 15,
      transformResponse: (response) => (Array.isArray(response?.result) ? response.result : []),
      providesTags: ['Weather'],
    }),
    getHurricanesLive: builder.query({
      query: () => ({
        url: '/v2/custom-weather/hurricanes/live',
      }),
      keepUnusedDataFor: 60 * 15,
      transformResponse: (response) => (Array.isArray(response?.result) ? response.result : []),
      providesTags: ['Weather'],
    }),
  }),
});

export const {
  useGetHourlyForecastQuery,
  useGetConditionsbyCityQuery,
  useGetDetailedForecastQuery,
  useGet15DayForecastQuery,
  useGet7DayHourlyForecastQuery,
  useGetAstronomyDataQuery,
  useGetEarthquakesQuery,
  useGetHurricanesHistoricalQuery,
  useGetHurricanesLiveQuery,
  useGetAirQualityForecastQuery,
  useGetAirQualityObservationQuery,
} = weatherApi;
